import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Check, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const digits = (v) => v.replace(/\D/g, "").slice(0, 4);

export default function Caves() {
  const { refreshMe } = useAuth();
  const [caves, setCaves] = useState([]);
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const load = () => api.get("/caves").then((r) => setCaves(r.data));
  useEffect(() => {
    load();
  }, []);

  const run = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      load();
      refreshMe();
      window.dispatchEvent(new Event("caves:changed"));
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };

  const add = async (e) => {
    e.preventDefault();
    if (await run(() => api.post("/caves", { name, pin }), "Cellar created")) {
      setName("");
      setPin("");
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Caves</p>
      <h2 className="font-serif text-4xl sm:text-5xl text-[#F5F0EB] mb-3">Cellars</h2>
      <p className="text-[#A89F91] mb-8 text-sm">Each cellar has its own wines, locations, boxes and movements. Anyone with a cellar's 4-digit code can open it. Changing a code signs out everyone using the old one. A sub-cellar (for example one house of a client) uses the code of its main cellar.</p>
      <form onSubmit={add} className="grid sm:grid-cols-[1fr_160px_auto] gap-3 mb-8">
        <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Cellar name" className="h-11 bg-[#161214]" data-testid="cave-name-input" />
        <Input required value={pin} onChange={(e) => setPin(digits(e.target.value))} inputMode="numeric" placeholder="4-digit code" className="h-11 bg-[#161214] font-mono tracking-widest" data-testid="cave-pin-input" />
        <Button type="submit" disabled={pin.length !== 4} className="h-11 bg-[#5C1D24] hover:bg-[#8C2B36] text-[#F5F0EB]" data-testid="add-cave-button">Create cellar</Button>
      </form>
      <div className="rounded-xl border border-[#2E252A] divide-y divide-[#2E252A]" data-testid="caves-list">
        {caves.filter((c) => !c.parent_id).map((c) => (
          <div key={c.id}>
            <CaveRow cave={c} run={run} />
            {caves.filter((s) => s.parent_id === c.id).map((s) => <div key={s.id} className="pl-8 border-t border-[#2E252A]"><CaveRow cave={s} run={run} /></div>)}
          </div>
        ))}
      </div>
    </div>
  );
}

const CaveRow = ({ cave, run }) => {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(cave.name);
  const [pin, setPin] = useState("");
  if (edit) {
    const save = async () => {
      if (await run(() => api.put(`/caves/${cave.id}`, { name, pin: pin || null }), "Cellar updated")) setEdit(false);
    };
    return (
      <div className="flex flex-wrap sm:flex-nowrap gap-2 p-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`cave-edit-name-${cave.id}`} />
        {!cave.parent_id && <Input value={pin} onChange={(e) => setPin(digits(e.target.value))} inputMode="numeric" placeholder="New code (optional)" className="sm:w-48 font-mono" data-testid={`cave-edit-pin-${cave.id}`} />}
        <Button size="icon" variant="ghost" disabled={pin.length > 0 && pin.length !== 4} onClick={save} data-testid={`cave-save-${cave.id}`}><Check className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => setEdit(false)}><X className="w-4 h-4" /></Button>
      </div>
    );
  }
  const addSub = () => {
    const sub = window.prompt(`Name of the new sub-cellar in ${cave.name} (e.g. a house)`);
    if (sub && sub.trim()) run(() => api.post("/caves", { name: sub.trim(), parent_id: cave.id }), "Sub-cellar created");
  };
  return (
    <div className="flex items-center gap-4 px-5 py-4" data-testid={`cave-row-${cave.id}`}>
      <div className="flex-1">
        <p className="text-[#F5F0EB]">{cave.parent_id && <span className="font-mono text-[10px] uppercase tracking-widest text-[#6E665D] mr-2">Sub-cellar</span>}{cave.name}</p>
        <p className="text-xs text-[#6E665D] font-mono">{cave.wines} wines · {cave.bottles} bottles · {cave.locations} locations</p>
      </div>
      {!cave.parent_id && <button onClick={addSub} className="text-xs text-[#A89F91] hover:text-[#D4AF37]" data-testid={`cave-add-sub-${cave.id}`}>+ Sub-cellar</button>}
      <button onClick={() => setEdit(true)} className="text-[#A89F91] hover:text-[#D4AF37]" data-testid={`cave-edit-${cave.id}`} aria-label="Edit"><Pencil className="w-4 h-4" /></button>
      <button onClick={() => window.confirm(`Delete ${cave.name}?`) && run(() => api.delete(`/caves/${cave.id}`), "Cellar deleted")} className="text-[#A89F91] hover:text-[#8C2B36]" data-testid={`cave-delete-${cave.id}`} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
    </div>
  );
};
